// DashboardList.tsx — la vue liste commune aux onglets Vidéos, Assets et Enregistrements
// (2026-10-22, document/dashboard-listes-propositions-ui-2026-10-22.md).
//
// Même grammaire partout : vignette + titre / sous-titre, état, créateur / ville, chiffres
// à droite, date / heure, actions. Les colonnes secondaires disparaissent sur écran étroit
// (leur contenu reste dans le sous-titre ou l'info-bulle) plutôt que d'imposer un
// ascenseur horizontal. Dans l'ordre par date, un séparateur collant par jour ; un clic
// sur un en-tête chiffré trie (le plus d'abord), un second revient à l'ordre par date.

import React, { useState } from 'react';

import list from './DashboardList.module.scss';
import { dayLabel, hour, ownerText, initials, plural, creatorPlace, CREATOR_PLACE_HINT } from './dashboardShared';

export interface Column<T> {
  id     : string;
  label  : string;
  title? : string;
  right? : boolean;
  /** Masquée sous 1280 px (`lg`) ou 1024 px (`md`). */
  hide?  : 'lg' | 'md';
  /** Tri de la colonne, le plus d'abord ; absent : en-tête inerte. */
  sort?  : (a: T, b: T) => number;
  cell   : (x: T) => React.ReactNode;
}

interface Props<T> {
  items    : T[];
  columns  : Column<T>[];
  rowKey   : (x: T) => React.Key;
  /** Attributs de la ligne (surlignage d'un point désigné sur la carte). */
  rowProps?: (x: T) => object;
  /** Date des séparateurs de jour ; les éléments arrivent déjà du plus récent au plus ancien. */
  dateOf   : (x: T) => string | null | undefined;
  noun     : { one: string; many: string };
  /** Tri piloté de l'extérieur (Vidéos garde le sien, en commun avec la vue cartes) ; null : par date. */
  sort?    : string | null;
  onSort?  : (id: string | null) => void;
}

const hideClass = (h?: 'lg' | 'md') => h === 'lg' ? list.hideLg : h === 'md' ? list.hideMd : '';

export function ListTable<T>({ items, columns, rowKey, rowProps, dateOf, noun, sort: outer, onSort }: Props<T>) {
  const [ inner, setInner ] = useState<string | null>(null);
  const sort    = onSort ? (outer ?? null) : inner;
  const setSort = (id: string | null) => onSort ? onSort(id) : setInner(id);

  // Un tri piloté sans colonne (Vidéos « Nom ») : l'ordre reçu, sans séparateurs.
  const column = columns.find(c => c.id === sort && c.sort);
  const byDate = sort === null;
  const rows   = column ? [...items].sort(column.sort) : items;

  // Effectif de chaque jour, pour les séparateurs.
  const perDay = new Map<string, number>();
  if (byDate) items.forEach(x => { const d = dayLabel(dateOf(x)); perDay.set(d, (perDay.get(d) ?? 0) + 1); });

  let lastDay = '';
  return (
    <div className={list.box}>
      <table className={list.table}>
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c.id} className={`${c.right ? list.right : ''} ${hideClass(c.hide)}`} title={c.title}
                  aria-sort={c.sort ? (sort === c.id ? 'descending' : 'none') : undefined}>
                {c.sort
                  ? <button type="button" className={`${list.sortBtn} ${sort === c.id ? list.sortOn : ''}`}
                            onClick={() => setSort(sort === c.id ? null : c.id)}
                            title={sort === c.id ? 'Revenir à l’ordre par date' : `Trier : ${c.label.toLowerCase()}`}>
                      {c.label}<span aria-hidden="true">{sort === c.id ? '▼' : '↕'}</span>
                    </button>
                  : c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(x => {
            const day = byDate ? dayLabel(dateOf(x)) : '';
            const sep = byDate && day !== lastDay;
            lastDay = day;
            return (
              <React.Fragment key={rowKey(x)}>
                {sep && <tr className={list.daySep}><td colSpan={columns.length}>{day}<span>{plural(perDay.get(day) ?? 0, noun.one, noun.many)}</span></td></tr>}
                <tr className={list.row} {...rowProps?.(x)}>
                  {columns.map(c => <td key={c.id} className={`${c.right ? `${list.right} ${list.num}` : ''} ${hideClass(c.hide)}`}>{c.cell(x)}</td>)}
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Cellules ─────────────────────────────────────────────────────────────────

/** Vignette (fournie par l'onglet) + titre en gras + sous-titre gris. */
export const TitleCell: React.FC<{ thumb: React.ReactNode; title: React.ReactNode; sub?: React.ReactNode; titleHint?: string }> =
  ({ thumb, title, sub, titleHint }) => (
  <div className={list.titleCell}>
    <div className={list.thumb}>{thumb}</div>
    <div className={list.lines}>
      <span className={list.title} title={titleHint}>{title}</span>
      {sub && <span className={list.sub}>{sub}</span>}
    </div>
  </div>
);

/** Créateur, sa ville dessous (connexion précédant la création). */
export const CreatorCell: React.FC<{ owner: string; email: string; geo?: { city: string | null; country: string | null } | null }> =
  ({ owner, email, geo }) => {
  const name  = ownerText(owner, email);
  const place = creatorPlace(geo);
  return (
    <div className={list.creator}>
      <span className={list.avatar}>{initials(name)}</span>
      <div className={list.lines}>
        <span className={list.strong} title={email}>{name}</span>
        <span className={list.sub} title={CREATOR_PLACE_HINT}>{place || 'Ville inconnue'}</span>
      </div>
    </div>
  );
};

/** « Aujourd'hui » / « Hier » / date, l'heure dessous. */
export const WhenCell: React.FC<{ iso: string | null | undefined }> = ({ iso }) => (
  <div className={list.lines}>
    <span className={list.strong}>{dayLabel(iso)}</span>
    <span className={list.sub}>{hour(iso)}</span>
  </div>
);

/** Un tiret discret pour une valeur absente. */
export const Dash = () => <span className={list.dash}>—</span>;

/** Actions de la ligne : la principale toujours, les autres au survol (toujours au toucher). */
export const RowActions: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className={list.actions}>{children}</div>;
